// NEETCutoffAnalysisEbook.jsx
import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import Edvisor_Logo_main from "../src/images/Edvisor_Logo_Black.png";
import ProspectusPDF from "../src/images/Prospectus.pdf";

// ========== CONFIGURATION ==========
const EMAILJS_SERVICE_ID = "service_88rhybd";
const EMAILJS_TEMPLATE_ID = "template_ma0mry3";
const EMAILJS_PUBLIC_KEY = "9DOL1n9gEVRi7rv5w";
const TARGET_EMAIL = "dataads@edvisor.co.in";

// ========== OTP API CONFIGURATION (Rebliss SMS) ==========
const REBLISS_SMS_URL = "https://edvisor.proinnovativesoftware.in/Api/Rebliss/Sms";

const sendOTPviaRebliss = async (phoneNumber, otp) => {
  const cleanPhone = phoneNumber.replace(/\D/g, '').slice(-10);
  console.log("📤 Sending OTP to", cleanPhone, "->", otp);
  try {
    await fetch(REBLISS_SMS_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ Number: cleanPhone, OTP: otp }).toString()
    });
  } catch (error) {
    console.log("API call attempted");
  }
  alert(`🔐 Your OTP is send.Please enter this code to verify your mobile number.`);
  return { success: true, message: "OTP sent successfully" };
};

const generateOTP = () => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

// ========== COLOR PALETTE (consistent with Edvisor brand) ==========
const COLORS = {
  green: "#4aad52",
  greenDark: "#2e8e36",
  greenLight: "#6dd97a",
  orange: "#f0a020",
  blue: "#2fa8d5",
  blueLight: "#5dc8ea",
  navy: "#0d2240",
  bgLight: "#f4faf5",
  white: "#ffffff",
  text: "#0d2240",
  textMuted: "#5a7a6a",
  border: "#c8e8cc",
  danger: "#ef4444",
};

// ========== HELPER: SEND LEAD EMAIL ==========
const sendLeadEmail = async (leadData, requestType) => {
  try {
    const templateParams = {
      to_email: TARGET_EMAIL,
      lead_type: requestType,
      name: leadData.name,
      email: leadData.email,
      phone: leadData.phone,
      neet_rank: leadData.neetRank || "Not provided",
      ebook_request: leadData.ebookType || "NEET Cutoff Analysis",
      page_url: window.location.href,
      timestamp: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }),
      user_agent: navigator.userAgent,
    };
    await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
    return true;
  } catch (err) {
    console.error("Email send error:", err);
    return false;
  }
};

// ========== PDF VIEWER MODAL (unchanged) ==========
function PDFViewerModal({ isOpen, onClose, pdfUrl }) {
  if (!isOpen) return null;

  return (
    <div style={modalOverlayStyle}>
      <div style={{ ...modalContainerStyle, maxWidth: 900, width: "95%" }}>
        <div style={modalHeaderStyle}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#fff" }}>📘 NEET Cutoff Analysis Ebook</div>
            <div style={{ fontSize: 12, color: "#a8e8b0", marginTop: 4 }}>Complete state-wise & college-wise NEET cutoff guide</div>
          </div>
          <button onClick={onClose} style={closeButtonStyle}>&times;</button>
        </div>
        <div style={{ padding: "16px", background: COLORS.white, height: "70vh", overflowY: "auto" }}>
          <iframe 
            src={pdfUrl} 
            title="NEET Cutoff Analysis Ebook" 
            style={{ width: "100%", height: "100%", border: "none", minHeight: "500px" }}
          />
        </div>
        <div style={{ padding: "16px 24px", background: "#f0fdf4", borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <a href={pdfUrl} download style={{ textDecoration: "none" }}>
            <button style={{ background: COLORS.greenDark, border: "none", padding: "10px 24px", borderRadius: 40, color: "#fff", fontWeight: 600, cursor: "pointer" }}>
              ⬇️ Download PDF
            </button>
          </a>
          <button onClick={onClose} style={{ background: "transparent", border: `2px solid ${COLORS.border}`, padding: "10px 24px", borderRadius: 40, color: COLORS.text, fontWeight: 600, cursor: "pointer" }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ========== MODAL FOR EBOOK DOWNLOAD (UPDATED WITH INLINE OTP) ==========
function EbookModal({ isOpen, onClose, onSubmit }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [neetRank, setNeetRank] = useState('');
  const [otp, setOtp] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [errors, setErrors] = useState({});
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Countdown timer
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0 && resendDisabled) {
      setResendDisabled(false);
    }
    return () => clearTimeout(timer);
  }, [countdown, resendDisabled]);

  // Reset modal when closed
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setEmail('');
      setPhone('');
      setNeetRank('');
      setOtp('');
      setGeneratedOtp('');
      setErrors({});
      setIsSendingOtp(false);
      setOtpSent(false);
      setVerified(false);
      setCountdown(0);
      setResendDisabled(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = 'Full name is required';
    if (!email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email is invalid';
    const cleanPhone = phone.replace(/\D/g, '').slice(-10);
    if (!cleanPhone || cleanPhone.length !== 10) newErrors.phone = 'Enter a valid 10-digit phone number';
    if (!neetRank.trim()) newErrors.neetRank = 'NEET Rank/AIR is required';
    else if (!/^\d+$/.test(neetRank.replace(/,/g, ''))) newErrors.neetRank = 'Enter a valid rank number';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOtp = async () => {
    const cleanPhone = phone.replace(/\D/g, '').slice(-10);
    if (!cleanPhone || cleanPhone.length !== 10) {
      setErrors({ phone: 'Enter a valid 10-digit phone number' });
      return;
    }
    setIsSendingOtp(true);
    const newOtp = generateOTP();
    setGeneratedOtp(newOtp);
    const result = await sendOTPviaRebliss(cleanPhone, newOtp);
    if (result.success) {
      setOtpSent(true);
      setCountdown(60);
      setResendDisabled(true);
      setErrors({});
    } else {
      setErrors({ phone: result.message });
      alert(`Failed to send OTP: ${result.message}`);
    }
    setIsSendingOtp(false);
  };

  const handleVerifyOtp = () => {
    if (!otp || otp.length !== 6) {
      setErrors({ otp: 'Enter valid 6-digit OTP' });
      return;
    }
    if (otp === generatedOtp) {
      setVerified(true);
      setErrors({});
      alert('Phone number verified successfully!');
    } else {
      setErrors({ otp: 'Invalid OTP. Please try again.' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!verified) {
      setErrors({ phone: 'Please verify your phone number via OTP first.' });
      alert('Please click "Send OTP" and verify your number first');
      return;
    }
    setIsSubmitting(true);
    const leadData = {
      name,
      email,
      phone,
      neetRank,
      otpVerified: true,
      ebookType: "NEET Cutoff Analysis Ebook"
    };
    const success = await sendLeadEmail(leadData, "NEET Cutoff Ebook Download (OTP Verified)");
    setIsSubmitting(false);
    if (success) {
      onSubmit(leadData);
      onClose();
    } else {
      alert("Unable to process request. Please try again later.");
    }
  };

  if (!isOpen) return null;

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContainerStyle}>
        <div style={modalHeaderStyle}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#fff" }}>📊 Download NEET Cutoff Analysis Ebook</div>
            <div style={{ fontSize: 12, color: "#a8e8b0", marginTop: 4 }}>Complete state-wise & college-wise NEET cutoff guide</div>
          </div>
          <button onClick={onClose} style={closeButtonStyle}>&times;</button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: '24px', background: COLORS.white }}>
          {/* Full Name */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Full Name <span style={{ color: COLORS.danger }}>*</span></label>
            <input 
              type="text" 
              value={name} 
              onChange={e => setName(e.target.value)} 
              placeholder="e.g. Priya Sharma" 
              style={inputStyle(errors.name)} 
            />
            {errors.name && <div style={errorStyle}>{errors.name}</div>}
          </div>

          {/* Email Address */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Email Address <span style={{ color: COLORS.danger }}>*</span></label>
            <input 
              type="email" 
              value={email} 
              onChange={e => setEmail(e.target.value)} 
              placeholder="priya@example.com" 
              style={inputStyle(errors.email)} 
            />
            {errors.email && <div style={errorStyle}>{errors.email}</div>}
          </div>

          {/* Phone Number with inline Send OTP */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Phone Number <span style={{ color: COLORS.danger }}>*</span></label>
            <div style={{ display: 'flex', gap: 10 }}>
              <input 
                type="tel" 
                value={phone} 
                onChange={e => setPhone(e.target.value)} 
                placeholder="9876543210" 
                style={{ ...inputStyle(errors.phone), flex: 1 }} 
              />
              <button 
                type="button" 
                onClick={handleSendOtp} 
                disabled={isSendingOtp || (otpSent && !verified && countdown > 0)}
                style={{
                  padding: "0 20px",
                  borderRadius: 12,
                  border: "none",
                  background: (isSendingOtp || (otpSent && !verified && countdown > 0)) ? "#ccc" : COLORS.blue,
                  color: "#fff",
                  fontWeight: 600,
                  cursor: (isSendingOtp || (otpSent && !verified && countdown > 0)) ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                  fontSize: "clamp(12px,3.5vw,13px)"
                }}
              >
                {isSendingOtp ? 'Sending...' : (otpSent && !verified ? `Resend ${countdown > 0 ? `(${countdown}s)` : ''}` : 'Send OTP')}
              </button>
            </div>
            {errors.phone && <div style={errorStyle}>{errors.phone}</div>}
          </div>

          {/* NEET Rank field */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Your NEET Rank / AIR <span style={{ color: COLORS.danger }}>*</span></label>
            <input 
              type="text" 
              value={neetRank} 
              onChange={e => setNeetRank(e.target.value)} 
              placeholder="e.g. 12450" 
              style={inputStyle(errors.neetRank)} 
            />
            {errors.neetRank && <div style={errorStyle}>{errors.neetRank}</div>}
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
              We use this to personalize college suggestions in the ebook
            </div>
          </div>

          {/* OTP Input + Verify (appears only after OTP sent) */}
          {otpSent && !verified && (
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Enter OTP <span style={{ color: COLORS.danger }}>*</span></label>
              <div style={{ display: 'flex', gap: 10 }}>
                <input 
                  type="text" 
                  value={otp} 
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} 
                  placeholder="6-digit OTP" 
                  maxLength="6"
                  style={{ ...inputStyle(errors.otp), flex: 1 }} 
                />
                <button 
                  type="button" 
                  onClick={handleVerifyOtp} 
                  style={{
                    padding: "0 20px",
                    borderRadius: 12,
                    border: "none",
                    background: COLORS.green,
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                    whiteSpace: "nowrap"
                  }}
                >
                  Verify
                </button>
              </div>
              {errors.otp && <div style={errorStyle}>{errors.otp}</div>}
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 6 }}>
                Enter the 6-digit code sent to your phone
              </div>
            </div>
          )}

          {/* Verified success message */}
          {verified && (
            <div style={{ color: COLORS.green, fontSize: 12, marginBottom: 16, textAlign: "center" }}>
              ✓ Phone number verified successfully!
            </div>
          )}

          {/* Submit Button – matches image style (green gradient, lock emoji) */}
          <button 
            type="submit" 
            style={{ 
              width: "100%", 
              background: `linear-gradient(90deg, ${COLORS.greenDark}, ${COLORS.green})`, 
              padding: "14px", 
              borderRadius: 40, 
              color: "#fff", 
              fontWeight: 700, 
              border: "none", 
              cursor: isSubmitting ? "not-allowed" : "pointer",
              fontSize: "clamp(15px,4vw,16px)",
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              opacity: isSubmitting ? 0.7 : 1
            }}
            disabled={isSubmitting}
          >
            🔒 {isSubmitting ? "Processing..." : "Download Ebook"}
          </button>

          <p style={{ fontSize: 11, color: COLORS.textMuted, textAlign: 'center', marginTop: 16 }}>
            We respect your privacy. No spam, ever. All leads sent to <strong style={{ color: COLORS.text }}>dataads@edvisor.co.in</strong>
          </p>
        </form>
      </div>
    </div>
  );
}

// ========== MODAL STYLES (Shared) ==========
const modalOverlayStyle = {
  position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
  background: "rgba(0,0,0,0.7)", backdropFilter: "blur(5px)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 10000, fontFamily: "'Poppins', sans-serif"
};
const modalContainerStyle = {
  background: COLORS.white, borderRadius: 24, maxWidth: 450, width: "90%",
  boxShadow: "0 20px 40px rgba(0,0,0,0.3)", overflow: "hidden",
  animation: "edv-fadeIn 0.3s ease"
};
const modalHeaderStyle = {
  background: `linear-gradient(135deg, ${COLORS.navy}, #1a3a6c)`,
  padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center"
};
const closeButtonStyle = { background: "none", border: "none", color: "#fff", fontSize: 28, cursor: "pointer", lineHeight: 1, padding: "0 8px" };
const labelStyle = { fontSize: 13, fontWeight: 600, color: COLORS.text, display: "block", marginBottom: 6 };
const inputStyle = (err) => ({
  width: "100%", border: `1.5px solid ${err ? COLORS.danger : COLORS.border}`, borderRadius: 12,
  padding: "12px 14px", fontSize: 14, outline: "none", fontFamily: "inherit",
  background: "#f9fafb", color: COLORS.text
});
const errorStyle = { color: COLORS.danger, fontSize: 11, marginTop: 4 };

// ========== MAIN COMPONENT (unchanged except modal usage) ==========
export default function NEETCutoffAnalysisEbook() {
  const [isEbookModalOpen, setIsEbookModalOpen] = useState(false);
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [activeCountry, setActiveCountry] = useState(null);

  // Hero background image URL (medical/students theme)
  const heroBgImageUrl = "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1600";

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes edv-spin { to { transform: rotate(360deg); } }
      @keyframes edv-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      @keyframes edv-fadeIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Poppins', sans-serif; background: #f4faf5; overflow-x: hidden; }
      .hero-grid { display: grid; grid-template-columns: 1fr 420px; gap: 40px; align-items: center; }
      @media (max-width: 1100px) { .hero-grid { grid-template-columns: 1fr !important; text-align: center; gap: 2rem; } }
      @media (max-width: 768px) { .hero-grid { padding: 30px 16px !important; } .hero-text h1 { font-size: 1.8rem !important; } }
      .marquee { white-space: nowrap; width: max-content; animation: edv-marquee 30s linear infinite; }
    `;
    document.head.appendChild(style);
    emailjs.init(EMAILJS_PUBLIC_KEY);
    return () => document.head.removeChild(style);
  }, []);

  const handleDownload = (leadData) => {
    console.log(`Ebook requested by ${leadData.name} at ${leadData.email}`);
    // Open the local PDF viewer modal after successful lead submission
    setIsPdfViewerOpen(true);
  };

  const openWhatsApp = (type) => {
    const msg = type === 'help' ? "Hello, I need help understanding NEET cutoffs." : "Hello, I'd like to know more about EdVisor's services.";
    window.open(`https://wa.me/918584826222?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const countries = [
    "RUSSIA", "BANGLADESH", "KAZAKHSTAN", "CHINA", "NEPAL", "UZBEKISTAN", "KYRGYZSTAN", "EGYPT", "GEORGIA"
  ];

  const stats = [
    { icon: "📅", number: "18+", label: "Years of Experience" },
    { icon: "👨‍🎓", number: "50k+", label: "Student Counselled" },
    { icon: "🌍", number: "100+", label: "University Worldwide" },
    { icon: "😊", number: "30k+", label: "Happy Student" },
    { icon: "🌐", number: "93+", label: "Clients Worldwide" }
  ];

  const services = [
    { icon: "🎯", title: "Career Counselling", desc: "Master career counselling for admission abroad to help you understand your maximum capacity." },
    { icon: "🤝", title: "Counselling Assistance", desc: "Customised counselling to explore admission challenges abroad, ensuring each step is consistent and well arranged." },
    { icon: "📚", title: "Course & College info", desc: "Top-to-bottom examinations of academic projects to assist you in choosing the right course and college." },
    { icon: "💰", title: "Financial Estimation", desc: "Concentrated financial overview of costs to guarantee you are financially prepared for your educational journey." },
    { icon: "✈️", title: "Study Abroad", desc: "Documentation administration ensuring a problem-free application process, from forms to visa requirements." },
    { icon: "🎓", title: "Admission Guidance", desc: "Placement assistance to help you progress from academic life to your professional career." }
  ];

  const courses = [
    { name: "MBBS", description: "Bachelor of Medicine, Bachelor of Surgery – study at top medical universities worldwide." },
    { name: "MBA", description: "Master of Business Administration – global leadership and management programs." },
    { name: "BTech", description: "Bachelor of Technology – engineering degrees with international exposure." },
    { name: "Pharma", description: "Pharmacy courses – B.Pharma, M.Pharma with research opportunities." },
    { name: "Nursing", description: "B.Sc Nursing & related healthcare programs with global curriculum." },
    { name: "Hotel Management", description: "International hospitality and tourism management courses." }
  ];

  const faqs = [
    { q: "What is NEET cutoff and why is it important?", a: "NEET cutoff is the minimum qualifying percentile/score required for admission to MBBS/BDS courses. It helps you understand which colleges you might get based on your rank and category." },
    { q: "Does this ebook cover state and central quotas?", a: "Yes! The ebook includes detailed cutoff analysis for AIQ (15%), State Quotas (85%), and also for deemed/private universities." },
    { q: "Is the cutoff data based on previous years?", a: "We provide analysis of last 5 years' cutoffs with trend predictions for 2026, helping you make informed decisions." },
    { q: "Can I get personalized college suggestions?", a: "Absolutely. After downloading, you can request a free counselling session where our experts will give you a personalized list based on your rank." },
    { q: "Does EdVisor help with study abroad for MBBS?", a: "Yes, we counsel for top medical universities in Russia, Bangladesh, Kazakhstan, Philippines, Georgia, and many more countries." },
    { q: "Is the ebook really free?", a: "100% free. No hidden charges. Just provide your details to access the comprehensive 100+ page PDF." }
  ];

  const testimonials = [
    { name: "Dr. Ananya Sharma", text: "EdVisor's cutoff ebook helped me shortlist colleges within my rank. I secured a seat in GMC Kolkata thanks to their guidance.", rating: 5, year: "MBBS 2025" },
    { name: "Rahul Mehta", text: "The detailed state-wise analysis saved me lakhs in counseling fees. Highly recommended for every NEET aspirant.", rating: 4.5, year: "MBBS Aspirant" },
    { name: "Priya Singh", text: "The team was very supportive. They not only provided the ebook but also connected me with alumni for honest reviews.", rating: 5, year: "Selected in Maharashtra Quota" }
  ];

  return (
    <div style={{ fontFamily: "'Poppins', sans-serif", background: COLORS.bgLight, color: COLORS.text, minHeight: "100vh", animation: "edv-fadeIn 0.5s ease" }}>
      <EbookModal isOpen={isEbookModalOpen} onClose={() => setIsEbookModalOpen(false)} onSubmit={handleDownload} />
      <PDFViewerModal isOpen={isPdfViewerOpen} onClose={() => setIsPdfViewerOpen(false)} pdfUrl={ProspectusPDF} />

      {/* Top Bar */}
      <div style={{ background: COLORS.navy, color: "#a8c8d0", fontSize: "clamp(11px,3vw,12px)", padding: "6px 0" }}>
        <div style={{ maxWidth: 1200, margin: "auto", padding: "0 20px", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
          <span>📊 NEET 2026 Cutoff Analysis Ebook – Live Now! &nbsp;|&nbsp;
            <button onClick={() => setIsEbookModalOpen(true)} style={{ background: "none", border: "none", color: "#7ee8a0", cursor: "pointer", textDecoration: "underline", font: "inherit" }}>Download Free PDF</button>
          </span>
          <span>
            <button onClick={() => openWhatsApp('help')} style={{ background: "none", border: "none", color: "#7ec8e8", cursor: "pointer", textDecoration: "underline", font: "inherit" }}>Help</button>
            &nbsp;|&nbsp;
            <button onClick={() => setShowAbout(!showAbout)} style={{ background: "none", border: "none", color: "#7ec8e8", cursor: "pointer", textDecoration: "underline", font: "inherit" }}>About EdVisor</button>
          </span>
        </div>
      </div>

      {/* Navbar with Logo */}
      <nav style={{ background: COLORS.white, boxShadow: "0 2px 12px rgba(0,0,0,0.09)", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: 1200, margin: "auto", padding: "0 16px", display: "flex", alignItems: "center", flexWrap: "wrap", minHeight: 62, gap: 12 }}>
          <a href="#" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none", flexShrink: 0 }}>
            <img src={Edvisor_Logo_main} alt="EdVisor Logo" style={{ height: 50, width: "auto" }} />
          </a>
          <div style={{ display: "flex", gap: 6, marginLeft: "auto", flexWrap: "wrap" }}>
            {["Cutoff Analysis", "State-wise Cutoffs", "College Predictor", "Study Abroad"].map(l => (
              <a key={l} href="#" style={{ textDecoration: "none", color: COLORS.text, fontSize: "clamp(12px,3.5vw,13.5px)", fontWeight: 600, padding: "6px 10px", borderRadius: 8 }}>{l}</a>
            ))}
          </div>
        </div>
      </nav>

      {/* Marquee */}
      <div style={{ background: COLORS.navy, color: "#7ec8e8", padding: "8px 0", overflow: "hidden" }}>
        <div style={{ display: "flex", gap: 50, animation: "edv-marquee 30s linear infinite", whiteSpace: "nowrap", width: "max-content" }}>
          {["📊 NEET 2026 Cutoff Analysis Ebook: 100+ pages", "🏥 State-wise closing ranks for MBBS/BDS", "🎯 College predictor based on your rank", "📞 Free Counselling: Call now!", "🌍 MBBS Abroad cutoffs and options", "✅ AIQ + State Quota + Deemed Universities", "📈 Trends from last 5 years"].map((item, i) => <span key={i} style={{ fontSize: "clamp(11px,3vw,12.5px)" }}>{item} &nbsp;|&nbsp;</span>)}
        </div>
      </div>

      {/* Hero Section */}
      <div className="hero-grid" style={{
        backgroundImage: `url(${heroBgImageUrl})`,
        backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed",
        padding: "clamp(60px,12vw,100px) 20px", position: "relative",
      }}>
        <div style={{ position: "absolute", inset: 0, background: "rgba(10,26,46,0.88)" }} />
        <div style={{ maxWidth: 1200, margin: "auto", display: "contents" }}>
          <div className="hero-text" style={{ position: "relative", zIndex: 2 }}>
            <div style={{ display: "inline-flex", gap: 6, background: "rgba(74,173,82,0.2)", border: "1px solid rgba(74,173,82,0.5)", color: "#7ee888", fontSize: "clamp(10px,3vw,12px)", fontWeight: 700, padding: "4px 12px", borderRadius: 20, marginBottom: 16 }}>📖 Comprehensive NEET Cutoff Guide</div>
            <h1 style={{ fontSize: "clamp(2rem,7vw,3rem)", fontWeight: 900, color: "#fff", lineHeight: 1.2, marginBottom: 14 }}>NEET 2026 <span style={{ color: "#6dd97a" }}>Cutoff Analysis Ebook</span></h1>
            <p style={{ color: "rgba(255,255,255,0.9)", fontSize: "clamp(14px,4vw,16px)", maxWidth: 540, marginBottom: 24 }}>Get complete state-wise, college-wise, and category-wise NEET cutoff trends. Predict your chances and plan your medical career with confidence.</p>
            <button onClick={() => setIsEbookModalOpen(true)} style={{ background: COLORS.green, border: "none", padding: "12px 28px", borderRadius: 40, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: "clamp(13px,4vw,14px)" }}>📥 Download Free Ebook →</button>
          </div>
          <div style={{ background: COLORS.white, borderRadius: 24, boxShadow: "0 25px 50px -12px rgba(0,0,0,0.45)", overflow: "hidden", position: "relative", zIndex: 2 }}>
            <div style={{ background: `linear-gradient(135deg, ${COLORS.green} 0%, ${COLORS.blue} 100%)`, padding: "18px 24px" }}>
              <div style={{ color: "#fff", fontSize: "clamp(15px,4vw,16px)", fontWeight: 800 }}>📖 NEET Cutoff Ebook – What's Inside?</div>
            </div>
            <div style={{ padding: "20px 24px 24px" }}>
              <ul style={{ margin: 0, paddingLeft: 20, color: COLORS.text, fontSize: "clamp(12px,3.5vw,13px)", display: "flex", flexDirection: "column", gap: 8 }}>
                <li>✅ State-wise closing ranks for all government colleges</li>
                <li>✅ AIQ (15%) quota cutoffs – last 5 years trend</li>
                <li>✅ Deemed & private university cutoff analysis</li>
                <li>✅ Category-wise (GEN/OBC/SC/ST/EWS) percentile data</li>
                <li>✅ MBBS/BDS expected cutoffs for 2026</li>
                <li>✅ Direct links to college admission portals</li>
              </ul>
              <button onClick={() => setIsEbookModalOpen(true)} style={{ width: "100%", background: `linear-gradient(90deg, ${COLORS.greenDark}, ${COLORS.green})`, marginTop: 20, padding: "12px", borderRadius: 12, color: "#fff", fontWeight: 600, border: "none", cursor: "pointer" }}>📊 Download Full Ebook (100+ Pages)</button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "clamp(20px,5vw,30px) 20px 60px" }}>
        
        {/* Stats Row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "clamp(12px,3vw,20px)", marginBottom: 50 }}>
          {stats.map((s, i) => (
            <div key={i} style={{ background: COLORS.white, borderRadius: 16, border: `1.5px solid ${COLORS.border}`, padding: "20px", textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{s.icon}</div>
              <div style={{ fontSize: "clamp(24px,5vw,30px)", fontWeight: 800, color: COLORS.greenDark }}>{s.number}</div>
              <div style={{ fontSize: "clamp(11px,3vw,12px)", color: COLORS.textMuted }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* "Bridging Your Dreams" Tagline from original site */}
        <div style={{ textAlign: "center", marginBottom: 50 }}>
          <div style={{ color: COLORS.green, fontWeight: 600, letterSpacing: 1, marginBottom: 8 }}>#BridgingYourDreamsToReality</div>
          <h2 style={{ fontSize: "clamp(1.5rem,5vw,2.2rem)", color: COLORS.navy, maxWidth: 800, margin: "0 auto" }}>Global & Indian Education Made Easy.</h2>
          <p style={{ color: COLORS.textMuted, marginTop: 12, fontSize: "clamp(13px,4vw,15px)" }}>Seamless education opportunities in India & abroad! Expert guidance to make your study journey effortless and successful.</p>
        </div>

        {/* Services Section */}
        <div style={{ marginBottom: 50 }}>
          <h2 style={{ textAlign: "center", fontSize: "clamp(1.6rem,5vw,2rem)", marginBottom: 20 }}>Our Services</h2>
          <div style={{ textAlign: "center", marginBottom: 32, color: COLORS.textMuted }}>Accelerate into the Future.</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 24 }}>
            {services.map((s, i) => (
              <div key={i} style={{ background: COLORS.white, borderRadius: 20, padding: "24px", border: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>{s.icon}</div>
                <h3 style={{ fontSize: "clamp(1.1rem,4vw,1.2rem)", marginBottom: 8 }}>{s.title}</h3>
                <p style={{ fontSize: "clamp(12px,3.5vw,13px)", color: COLORS.textMuted, lineHeight: 1.5 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Courses */}
        <div style={{ marginBottom: 50 }}>
          <h2 style={{ textAlign: "center", fontSize: "clamp(1.6rem,5vw,2rem)", marginBottom: 32 }}>Explore a World of Opportunities</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 20 }}>
            {courses.map((c, i) => (
              <div key={i} style={{ background: COLORS.white, borderRadius: 16, padding: "20px", border: `1px solid ${COLORS.border}` }}>
                <div style={{ fontWeight: 800, fontSize: "clamp(1.1rem,4vw,1.2rem)", color: COLORS.navy, marginBottom: 8 }}>{c.name}</div>
                <p style={{ fontSize: "clamp(11px,3.5vw,12.5px)", color: COLORS.textMuted }}>{c.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Countries we serve (interactive) */}
        <div style={{ background: COLORS.white, borderRadius: 28, padding: "clamp(30px,5vw,40px)", marginBottom: 50, boxShadow: "0 5px 20px rgba(0,0,0,0.05)" }}>
          <h2 style={{ textAlign: "center", fontSize: "clamp(1.4rem,5vw,1.8rem)", marginBottom: 12 }}>Your Trusted Education Partner</h2>
          <p style={{ textAlign: "center", color: COLORS.textMuted, marginBottom: 32 }}>Guiding Students Worldwide</p>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "12px" }}>
            {countries.map((c, i) => (
              <button key={i} onClick={() => setActiveCountry(c)} style={{ background: activeCountry === c ? COLORS.green : "#eee", border: "none", padding: "8px 18px", borderRadius: 40, fontWeight: 500, color: activeCountry === c ? "#fff" : COLORS.text, cursor: "pointer", transition: "0.2s" }}>{c}</button>
            ))}
          </div>
          {activeCountry && (
            <div style={{ marginTop: 28, padding: 20, background: COLORS.bgLight, borderRadius: 20, textAlign: "center", animation: "edv-fadeIn 0.2s" }}>
              <p style={{ color: COLORS.navy }}>📌 <strong>{activeCountry}</strong> – Top medical universities, affordable tuition, and globally recognized degrees. <button onClick={() => setIsEbookModalOpen(true)} style={{ background: "none", border: "none", color: COLORS.green, cursor: "pointer", fontWeight: 600, textDecoration: "underline" }}>Get cutoff details →</button></p>
            </div>
          )}
        </div>

        {/* CTA Banner */}
        <div style={{ background: `linear-gradient(105deg, ${COLORS.navy} 0%, #1a3a6c 100%)`, borderRadius: 28, padding: "clamp(30px,6vw,48px)", textAlign: "center", marginBottom: 50 }}>
          <div style={{ fontSize: "clamp(28px,6vw,40px)", marginBottom: 12 }}>🎯</div>
          <h2 style={{ color: "#fff", fontSize: "clamp(1.4rem,5vw,1.8rem)", marginBottom: 12 }}>Confused About Your NEET Rank & College Options?</h2>
          <p style={{ color: "rgba(255,255,255,0.85)", maxWidth: 600, margin: "0 auto 24px", fontSize: "clamp(13px,4vw,14px)" }}>Our expert counsellors will analyze your rank, category, and preferences to provide a personalized college list – absolutely free!</p>
          <button onClick={() => setIsEbookModalOpen(true)} style={{ background: COLORS.white, color: COLORS.navy, padding: "12px 32px", borderRadius: 40, fontWeight: 700, border: "none", fontSize: "clamp(13px,4vw,14px)", cursor: "pointer" }}>📞 Book Free Counselling Session</button>
        </div>

        {/* Testimonials */}
        <div style={{ marginBottom: 50 }}>
          <h2 style={{ textAlign: "center", fontSize: "clamp(1.6rem,5vw,2rem)", marginBottom: 32 }}>Students Share Their Success Stories</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
            {testimonials.map((t, i) => (
              <div key={i} style={{ background: COLORS.white, borderRadius: 20, padding: "24px", border: `1px solid ${COLORS.border}` }}>
                <div style={{ color: COLORS.orange, marginBottom: 12 }}>{"★".repeat(Math.floor(t.rating))}{t.rating % 1 ? "½" : ""}</div>
                <p style={{ fontStyle: "italic", color: COLORS.textMuted, marginBottom: 16 }}>"{t.text}"</p>
                <div><strong>{t.name}</strong> <span style={{ fontSize: 12, color: COLORS.green }}>– {t.year}</span></div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div style={{ marginBottom: 50 }}>
          <h2 style={{ textAlign: "center", fontSize: "clamp(1.6rem,5vw,2rem)", marginBottom: 32 }}>Some Queries You Think – We Answer.</h2>
          <div style={{ background: COLORS.white, borderRadius: 24, border: `1.5px solid ${COLORS.border}`, overflow: "hidden" }}>
            {faqs.map((f, i) => (
              <details key={i} style={{ borderBottom: i === faqs.length - 1 ? "none" : `1px solid ${COLORS.border}`, padding: "16px 24px" }}>
                <summary style={{ fontSize: "clamp(14px,4vw,15px)", fontWeight: 600, cursor: "pointer", color: COLORS.text }}>{f.q}</summary>
                <p style={{ marginTop: 12, fontSize: "clamp(12px,3.5vw,13px)", color: COLORS.textMuted, lineHeight: 1.6 }}>{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* About Section Toggle */}
        <div style={{ textAlign: "center", marginBottom: 30 }}>
          <button onClick={() => setShowAbout(!showAbout)} style={{ background: "transparent", border: `1.5px solid ${COLORS.green}`, color: COLORS.green, padding: "8px 28px", borderRadius: 30, fontWeight: 600, cursor: "pointer" }}>
            {showAbout ? "✖ Close" : "ℹ️ About EdVisor"}
          </button>
        </div>
        {showAbout && (
          <div style={{ background: COLORS.white, borderRadius: 24, padding: "clamp(24px,5vw,32px)", marginBottom: 40, textAlign: "center" }}>
            <h3 style={{ color: COLORS.navy, marginBottom: 16, fontSize: "clamp(1.3rem,4vw,1.6rem)" }}>About EdVisor</h3>
            <p style={{ fontSize: "clamp(13px,4vw,14px)", color: COLORS.textMuted, lineHeight: 1.6 }}>EdVisor is India's leading platform for NEET preparation and medical admission guidance. We help over 50,000 students annually with accurate answer keys, score calculators, and free expert counselling. Our mission: "Bridging Your Dreams to Reality". With 18+ years of experience and 100+ university partners, we ensure you get the right support to secure a seat in top medical colleges in India or abroad.</p>
            <div style={{ marginTop: 20, display: "flex", justifyContent: "center", gap: 20, flexWrap: "wrap" }}>
              <span>📍 Kolkata: 2C Dimple Court, 26 Shakespeare Sarani, Kolkata - 700017</span>
              <span>📍 Bangalore: 708, 7th Floor, North Rear Wing, Manipal Centre, M.G Road, Bangalore - 560001</span>
            </div>
            <p style={{ marginTop: 20, color: COLORS.green, fontSize: "clamp(12px,3.5vw,13px)" }}>🌍 www.edvisor.co.in | Mon-Sat: 10 AM – 8 PM</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer style={{ background: "#0a1a0e", color: "#6a9a72", padding: "40px 20px 20px" }}>
        <div style={{ maxWidth: 1200, margin: "auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "clamp(20px,5vw,30px)", marginBottom: 30 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <img src={Edvisor_Logo_main} alt="EdVisor Logo" style={{ height: 40, width: "auto", filter: "brightness(0) invert(1)" }} />
              </div>
              <p style={{ fontSize: "clamp(12px,3.5vw,13px)", lineHeight: 1.6 }}>India's most trusted NEET cutoff analysis & admission platform.</p>
              <div style={{ marginTop: 16, fontSize: "clamp(11px,3vw,12px)" }}>📞 +91 85848 26222<br/>✉️ dataads@edvisor.co.in</div>
            </div>
            <div>
              <h4 style={{ color: "#a0e8b0", marginBottom: 16 }}>Resources</h4>
              <a href="#" style={{ display: "block", color: "#6a9a72", textDecoration: "none", marginBottom: 8 }}>NEET Cutoff 2026</a>
              <a href="#" style={{ display: "block", color: "#6a9a72", textDecoration: "none", marginBottom: 8 }}>College Predictor</a>
              <a href="#" style={{ display: "block", color: "#6a9a72", textDecoration: "none", marginBottom: 8 }}>Rank Calculator</a>
              <a href="#" style={{ display: "block", color: "#6a9a72", textDecoration: "none" }}>Counselling Guide</a>
            </div>
            <div>
              <h4 style={{ color: "#a0e8b0", marginBottom: 16 }}>Quick Links</h4>
              <a href="#" style={{ display: "block", color: "#6a9a72", textDecoration: "none", marginBottom: 8 }}>About Us</a>
              <a href="#" style={{ display: "block", color: "#6a9a72", textDecoration: "none", marginBottom: 8 }}>Contact</a>
              <a href="#" style={{ display: "block", color: "#6a9a72", textDecoration: "none", marginBottom: 8 }}>Study Abroad</a>
              <a href="#" style={{ display: "block", color: "#6a9a72", textDecoration: "none" }}>MBBS Abroad</a>
            </div>
          </div>
          <div style={{ borderTop: "1px solid #1a3a1e", paddingTop: 20, textAlign: "center", fontSize: "clamp(10px,3vw,12px)" }}>
            © 2026 EdVisor India Pvt. Ltd. | NEET Cutoff Analysis Ebook for reference purposes only. Official cutoffs at nta.ac.in
          </div>
        </div>
      </footer>
    </div>
  );
}